import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { enumLabels, type EnumKind } from "./enumLabels";
import { messages, type MsgKey } from "./messages";
import type { Entry, InterpolationParams, Lang } from "./types";

function interpolate(template: string, params?: InterpolationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in params ? String(params[key]) : `{${key}}`,
  );
}

export interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MsgKey, params?: InterpolationParams) => string;
  te: (kind: EnumKind, value: string | null | undefined) => string;
  formatDateTime: (value: string | number | Date | null | undefined) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem("cs-lang");
    return stored === "en" ? "en" : "ko";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      setLang: (nextLang) => {
        localStorage.setItem("cs-lang", nextLang);
        setLangState(nextLang);
      },
      t: (key, params) => interpolate(messages[key][lang] ?? messages[key].ko, params),
      te: (kind, rawValue) => {
        if (!rawValue) return "-";
        const group = enumLabels[kind] as Record<string, Entry>;
        const entry = group[rawValue];
        return entry ? entry[lang] : rawValue;
      },
      formatDateTime: (dateValue) => {
        if (dateValue === null || dateValue === undefined) return "-";
        const locale = lang === "ko" ? "ko-KR" : "en-US";
        return new Date(dateValue).toLocaleString(locale);
      },
    }),
    [lang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
}
